/* eslint-disable no-param-reassign,react-hooks/exhaustive-deps,no-return-assign */
import React, { useEffect, useState } from "react";
import { Form, Tabs } from "antd";
import omit from "lodash/omit";
import Checkbox from "antd/es/checkbox";
import moment from "moment";
import PropertyModal from "./PropertyModal/PropertyModal";
import HistoryModal from "./HistoryModal/HistoryModal";
import ModalWindow from "../ModalWindow/ModalWindow";
import { infinityDateHttpFormat, toHttpFormat } from "../../lib/utils/formatDate";
import {
  fetchBillActionHistory,
  createProperty,
  deleteProperties,
  fetchProperties,
  fetchPropertiesVersions,
  updateProperty,
  propertiesActions,
} from "../../store/slices/properties";
import {
  fetchPropertyTypes,
  fetchPropertyTypesByObjectType,
} from "../../store/slices/property-type-admin";
import { fetchDicActionType, fetchDictionaries } from "../../store/slices/rtkDictionaries";
import { checkIsLoaded } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../..";
import { Property, PropertyType } from "../../lib/interfaces";
import { ObjectTypeSysname } from "../../lib/types";
import "./HistoryAndProperyModal.less";

interface IHistoryAndPropertyModal {
  objectTypeSysname: ObjectTypeSysname;
  objectId: number | null | undefined;
  handleHideModal: () => void;
  visibleModal: boolean;
  dateObjectFromForCalendar?: string;
  dateObjectToForCalendar?: string;
  hasAccessToEdit?: boolean;
}

const HistoryAndPropertyModal = ({
  objectTypeSysname,
  objectId,
  handleHideModal,
  visibleModal,
  dateObjectFromForCalendar,
  dateObjectToForCalendar,
  hasAccessToEdit = false,
}: IHistoryAndPropertyModal) => {
  const dispatch = useAppDispatch();
  const { TabPane } = Tabs;
  const [form] = Form.useForm();

  const { dictionariesLoading, actionTypeLoading } = useAppSelector(
    (state) => state.rtkDictionaries
  );

  const { propertyTypesLoading } = useAppSelector((state) => state.propertyTypesAdmin);

  const { objectTypes } = useAppSelector((state) => state.rtkDictionaries);

  const [formPropertyEditStatus, setFormPropertyEditStatus] = useState("");
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [customPropertyTypes, setCustomPropertyTypes] = useState<PropertyType[]>([]);
  const [closeDateInPast, setCloseDateInPast] = useState(false);
  const [editingKey, setEditingKey] = useState<string>();
  const [activeTab, setActiveTab] = useState("1");
  const [checkedShowClosedProps, setCheckedShowClosedProps] = useState(false);

  const isMakingNewProperty = formPropertyEditStatus === "createNewProperty";
  const isEditingPropertyToDate = formPropertyEditStatus === "editingPropertyToDate";
  const isEditingForceProperty = formPropertyEditStatus === "editingForceProperty";
  const isMakingNewPropertyVersion = formPropertyEditStatus === "createNewPropertyVersion";

  const changeTab = (activeKey) => {
    setActiveTab(activeKey);
  };

  const defaultPropertiesRequestBody: Partial<Property> = {
    id: undefined,
    stringValue: undefined,
    numberValue: undefined,
    dateValue: undefined,
    dictionaryValue: undefined,
    specificDicId: undefined,
    fromDate: undefined,
    toDate: undefined,
    objectTypeSysname: undefined,
    propertyTypeAlias: undefined,
    dsc: undefined,
  };

  const [propertiesRequestBody, setPropertiesRequestBody] = useState<Partial<Property>>(
    defaultPropertiesRequestBody
  );

  const handleSetPropertiesRequestBody = (props: Partial<Property>) => {
    setPropertiesRequestBody((prev) => ({ ...prev, ...props }));
    if (props.dateValue) {
      form.setFieldsValue({ ...props, dateValue: moment(props.dateValue) });
    } else {
      form.setFieldsValue(props);
    }
  };

  const resetPropertiesRequestBody = () => {
    setPropertiesRequestBody(defaultPropertiesRequestBody);
    form.resetFields();
  };

  useEffect(() => {
    if (visibleModal && objectId !== undefined && objectId !== null) {
      dispatch(fetchBillActionHistory({ objectTypeSysname, objectId }));

      dispatch(
        fetchProperties({ objectTypeSysname, objectId, includeClosed: checkedShowClosedProps })
      );
      dispatch(fetchPropertyTypesByObjectType({ objectTypeSysname }));

      if (!checkIsLoaded(dictionariesLoading)) {
        dispatch(fetchDictionaries());
      }

      if (!checkIsLoaded(actionTypeLoading)) {
        dispatch(fetchDicActionType());
      }

      if (!checkIsLoaded(propertyTypesLoading)) {
        dispatch(fetchPropertyTypes());
      }
    } else if (!visibleModal) {
      dispatch(propertiesActions.cleanBillActionHistory());
      dispatch(propertiesActions.cleanProperties());
    }
  }, [objectId, objectTypeSysname, visibleModal]);

  const handleSubmit = () => {
    form.validateFields().then(async () => {
      const dateObjectToForCreateProperty = () => {
        if (dateObjectToForCalendar) {
          return toHttpFormat(dateObjectToForCalendar);
        }
        return toHttpFormat(infinityDateHttpFormat);
      };

      // return console.log({ propertiesRequestBody });

      if (isMakingNewProperty && objectId) {
        await dispatch(
          createProperty({
            objectTypeSysname,
            objectId,
            values: {
              ...propertiesRequestBody,
              toDate: dateObjectToForCreateProperty(),
            },
          })
        );
      }

      if (isEditingPropertyToDate && propertiesRequestBody.id && propertiesRequestBody.toDate) {
        await dispatch(
          deleteProperties({
            id: propertiesRequestBody.id,
            fromDate: propertiesRequestBody.toDate,
          })
        );
        if (closeDateInPast) setExpandedRowKeys([]);
      }

      if (
        isMakingNewPropertyVersion &&
        propertiesRequestBody.id &&
        propertiesRequestBody.fromDate
      ) {
        const values = omit(propertiesRequestBody, "specificDicType") as Property;
        await dispatch(
          updateProperty({
            id: propertiesRequestBody.id,
            isVersionUpdated: false,
            fromDate: propertiesRequestBody.fromDate,
            values,
          })
        );
      }

      if (isEditingForceProperty && propertiesRequestBody.id && propertiesRequestBody.fromDate) {
        const values = omit(propertiesRequestBody, "specificDicType", "parentVersionSpecificDicId");
        await dispatch(
          updateProperty({
            id: propertiesRequestBody.id,
            isVersionUpdated: true,
            fromDate: propertiesRequestBody.fromDate,
            values,
          })
        );
      }

      if (objectId) {
        await dispatch(
          fetchProperties({
            objectTypeSysname,
            objectId,
            includeClosed: checkedShowClosedProps,
          })
        );
      }

      if (propertiesRequestBody.id) {
        dispatch(fetchPropertiesVersions({ id: propertiesRequestBody.id }));
      }

      setEditingKey(undefined);
      setFormPropertyEditStatus("");

      resetPropertiesRequestBody();
    });
  };

  const closeModalAndResetStates = () => {
    handleHideModal();
    setFormPropertyEditStatus("");
    setEditingKey(undefined);
    setExpandedRowKeys([]);
    setCustomPropertyTypes([]);
    resetPropertiesRequestBody();
    changeTab("1");
    setCheckedShowClosedProps(false);
  };

  const onChecked = (e) => {
    setExpandedRowKeys([]);
    setFormPropertyEditStatus("");
    setCheckedShowClosedProps((prev) => !prev);
    if (objectId) {
      dispatch(fetchProperties({ objectTypeSysname, objectId, includeClosed: e.target.checked }));
    }
  };

  const getTabBarExtraContent = () => {
    const objectName =
      objectTypes.find(({ sysname }) => sysname === objectTypeSysname)?.name || "Object";

    return (
      <div style={{ display: "flex", justifyContent: "space-between", width: "345px" }}>
        <span style={{ fontWeight: "bold", fontSize: "16px" }}>
          {objectName} ID: {objectId}
        </span>
        {activeTab === "2" && (
          <Checkbox
            className="tab-extra-content"
            onChange={onChecked}
            checked={checkedShowClosedProps}
          >
            Показать закрытые
          </Checkbox>
        )}
      </div>
    );
  };

  const renderModalContent = () => (
    <Tabs
      activeKey={activeTab}
      onChange={changeTab}
      className="bill-history-modal-tabs"
      tabBarExtraContent={getTabBarExtraContent()}
    >
      <TabPane tab="История действий по объекту" key="1">
        <HistoryModal />
      </TabPane>

      <TabPane tab="Свойства" key="2">
        <PropertyModal
          propertiesRequestBody={propertiesRequestBody}
          handleSetPropertiesRequestBody={handleSetPropertiesRequestBody}
          resetPropertiesRequestBody={resetPropertiesRequestBody}
          setFormPropertyEditStatus={setFormPropertyEditStatus}
          setEditingKey={setEditingKey}
          editingKey={editingKey}
          expandedRowKeys={expandedRowKeys}
          setExpandedRowKeys={setExpandedRowKeys}
          isMakingNewProperty={isMakingNewProperty}
          isEditingPropertyToDate={isEditingPropertyToDate}
          isEditingForceProperty={isEditingForceProperty}
          isMakingNewPropertyVersion={isMakingNewPropertyVersion}
          dateObjectFromForCalendar={dateObjectFromForCalendar}
          dateObjectToForCalendar={dateObjectToForCalendar}
          customPropertyTypes={customPropertyTypes}
          setCustomPropertyTypes={setCustomPropertyTypes}
          setCloseDateInPast={setCloseDateInPast}
          handleSubmit={handleSubmit}
          checkedShowClosedProps={checkedShowClosedProps}
          hasAccessToEdit={hasAccessToEdit}
          form={form}
        />
      </TabPane>
    </Tabs>
  );

  if (objectId === null || objectId === undefined) return null;

  return (
    <>
      <ModalWindow
        className="account-bills__modal property-modal"
        width={1200}
        visible={visibleModal}
        handleCancel={closeModalAndResetStates}
        handleSubmit={closeModalAndResetStates}
        disabled={formPropertyEditStatus}
        footerHide
      >
        {renderModalContent()}
      </ModalWindow>
    </>
  );
};

export default HistoryAndPropertyModal;
